import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Modal, Select, TextInput, Toggle } from "@opendate-app/setlist";
import { useStaffingDepartmentsContext } from "./StaffingDepartmentsContext";
import ColorSwatches from "./ColorSwatches";
import { inScope } from "./staffingScopes";

const PositionModal = ({ position, onClose }) => {
  const { departments, permissions, permissionScopes, paths, mutate } = useStaffingDepartmentsContext();
  const isNew = !position.id;
  const [form, setForm] = useState({
    name: position.name || "",
    staff_department_id: position.staff_department_id || "",
    volunteer: Boolean(position.volunteer),
    default_hourly_rate: position.default_hourly_rate ?? "",
    color_hex: position.color_hex || "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const canSubmit = form.name.trim().length > 0 && !saving;
  const canSetRate = permissions.manageRates && (isNew || inScope(permissionScopes?.ratePositionIds, position.id));

  const departmentOptions = departments
    .filter((department) => !department.archived || department.id === position.staff_department_id)
    .filter((department) => department.id === position.staff_department_id || inScope(permissionScopes?.manageDepartmentIds, department.id))
    .map((department) => ({ value: department.id, label: department.name }));
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const attributes = {
      name: form.name,
      staff_department_id: form.staff_department_id || null,
      volunteer: form.volunteer,
      color_hex: form.color_hex || null,
    };
    if (canSetRate) {
      attributes.default_hourly_rate = form.volunteer || form.default_hourly_rate === "" ? null : form.default_hourly_rate;
    }
    const body = { staff_position: attributes };
    const result = isNew
      ? await mutate("post", paths.positions, body, "Could not add this position.")
      : await mutate("patch", `${paths.positions}/${position.id}`, body, "Could not save this position.");
    setSaving(false);
    if (result.ok) {
      onClose();
    } else {
      setErrors(result.errors);
    }
  };

  return (
    <Modal
      open
      size="sm"
      title={isNew ? "Add a position" : "Edit position"}
      description="A position is a job people work, like Bartender or Usher."
      onClose={onClose}
      footer={
        <div className="d-flex justify-content-end w-100" style={{ gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert color="error" title="Could not save" description={errors.join(" ")} />
        </div>
      )}
      <div className="mb-3">
        <TextInput id="staff-position-name" label="Name" required value={form.name} onChange={(event) => setField("name", event.target.value)} />
      </div>
      <div className="mb-3">
        <Select
          id="staff-position-department"
          label="Department"
          placeholder="General"
          options={departmentOptions}
          value={form.staff_department_id}
          onChange={(value) => setField("staff_department_id", value || "")}
        />
      </div>
      <div className="mb-3">
        <Toggle
          id="staff-position-volunteer"
          label="Volunteer position"
          description="Volunteers log hours but have no pay rate."
          checked={form.volunteer}
          onChange={(event) => setField("volunteer", event.target.checked)}
        />
      </div>
      {canSetRate && !form.volunteer && (
        <div className="mb-3">
          <TextInput
            id="staff-position-rate"
            label="Default hourly rate"
            hint="People can have their own rate for this position."
            type="number"
            min={0}
            step="0.01"
            leadingText="$"
            value={form.default_hourly_rate}
            onChange={(event) => setField("default_hourly_rate", event.target.value)}
          />
        </div>
      )}
      <ColorSwatches id="staff-position-color" label="Color in the schedule" value={form.color_hex} onChange={(value) => setField("color_hex", value)} />
    </Modal>
  );
};

PositionModal.propTypes = {
  position: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    staff_department_id: PropTypes.number,
    volunteer: PropTypes.bool,
    default_hourly_rate: PropTypes.string,
    color_hex: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PositionModal;
